import { Badge, Box, ComponentWithAs, Icon, IconButton, IconProps, Text, Tooltip, useDisclosure } from '@chakra-ui/react'
import React, { useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import type { IconType } from 'react-icons'
import { GiUpgrade } from 'react-icons/gi'
import { HiMiniBookmark } from "react-icons/hi2";
import { MdManageAccounts, MdOutlinePolicy } from 'react-icons/md'
import { TbLink } from 'react-icons/tb'
import { NavLink, useHistory } from 'react-router-dom'
import styled from 'styled-components'
import Collection from '../../../graphics/navbar/Collection'
import DeveloperOptions from '../../../graphics/navbar/DeveloperOptions'
import ExternalStorage from '../../../graphics/navbar/ExternalStorage'
import Home from '../../../graphics/navbar/Home'
import MediaLibrary from '../../../graphics/navbar/MediaLibrary'
import PerformanceCenter from '../../../graphics/navbar/PerformanceCenter'
import PurgeCache from '../../../graphics/navbar/PurgeCache'
import RightArrow from '../../../graphics/navbar/RightArrow'
import Setting from '../../../graphics/navbar/Setting'
import Upgrade from '../../../graphics/navbar/Upgrade'
import URLEndpoint from '../../../graphics/navbar/URLEndpoint'
import UsageAnalytics from '../../../graphics/navbar/UsageAnalytics'
import { colors } from '../../../ui-kit'
import { checkPermission, PermissionsEnum } from '../../../utils'
import { isRestrictedUser } from '../../../utils/roles'
import { getCheckListItems, useProductOnboardingMetrics } from '../../layouts/Home/Checklist'
import BookmarksDrawer from './BookmarksDrawer'
import { NavBarContext, UserContext } from '../../providers'
import SavedSearchesDrawer from './SavedSearchesDrawer'
import AddBookmarkModal from '../Bookmark/AddBookmarkModal'
import useFetch, { CachePolicies } from 'use-http'
import { ListBookmarkSuccessResponse } from '../../interfaces/BookmarksResponse'
import PermissionGuard from '../PermissionGuard'
import { GoPlus } from 'react-icons/go'

const NavWrapper = styled.nav<{ isOpen: boolean }>`
  width: 100%;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: left;
  color: ${colors.appPaleBlue};
  overflow-x: hidden;
  scrollbar-width: none;

  .navLink {
    padding: 0.5em 0.75em;
    white-space: nowrap;

    :hover {
      border-radius: 6px;
      background-color: ${colors.appSlate};
    }
  }

  .navLinkActive {
    border-radius: 6px;
    background-color: ${colors.appSlate};
    color: white;
  }
`

const NavGroupWrapper = styled.ul`
  width: 100%;
  list-style-type: none;
  border: none;
  object-fit: contain;
`

const NavItemWrapper = styled.li`
  width: 100%;
  display: flex;
  justify-content: center;
  flex-direction: column;

  .navLink {
    svg {
      vertical-align: middle;
    }
    cursor: pointer;
  }

  :not(:last-child) {
    margin-bottom: 0.25em;
  }
`

const NavLinkTextWrapper = styled.span<{ open: boolean }>`
  vertical-align: middle;
  line-height: 1.5;
  padding-left: 12px;
  font-weight: 500;
`

const NavGroupHeading = styled(Box) <{ open: boolean }>`
  text-transform: uppercase;
  font-weight: 400;
  color: #CDD5DF;
  font-size: 12px;
  line-height: 1.5;
  white-space: nowrap;
  transition: height var(--sidebarNavTransitionDuration) ease-in-out, margin-bottom var(--sidebarNavTransitionDuration) ease-in-out;
  // If not open the set height to 0
  height: ${props => (props.open ? '18px' : '0')};
  overflow: hidden;
  margin-bottom: ${props => (props.open ? '0.5em' : '0')};
`

const StyledUpgradeBox = styled(Box) <{ open: boolean }>`
  background: linear-gradient(107.38deg, rgba(202, 217, 239, 0.3) 0%, rgba(202, 217, 239, 0.1) 101.3%);
  padding: ${props => (props.open ? '12px 16px 12px 16px' : '8px')};
  border-radius: 12px;
  margin: 1em 0 1.75em 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: white;
  white-space: nowrap;
  transition: height var(--sidebarNavTransitionDuration) ease-in-out;
  height: ${props => (props.open ? '129px' : '40px')};
  overflow: hidden;
`

const HeadwayBadge = styled.span<{ open: boolean }>`
  position: absolute;
  ${props =>
    props.open ? 'margin-top: -5px;' : 'margin-top: -10px; margin-left: -12px;'}
`

interface NavItemProps {
  route: string
  icon: {
    icon: IconType | ComponentWithAs<'svg', IconProps>
    label: string
  }
  label: string
  isOpen: boolean
  onClick?: () => void
  showFinishSetupBadge?: boolean
  children?: JSX.Element
  highlight?: boolean
}

const NavItem: React.FC<NavItemProps> = ({
  route,
  icon,
  label,
  isOpen,
  children,
  onClick,
  showFinishSetupBadge,
  highlight,
}) => {
  const {
    isOpen: isOpenBookmarks,
    onClose: onCloseBookmarks,
    onOpen: onOpenBookmarks,
  } = useDisclosure()
  const { user } = useContext(UserContext)
  const { setBookmarks } = useContext(NavBarContext)
  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const getAndSetBookmarks = async () => {
    const res = (await request.get(
      `/api/media-library/${user?.clientNumber}/bookmarks`,
    )) as ListBookmarkSuccessResponse
    setBookmarks(res)
  }

  const content = (
    <>
      <Icon aria-label={icon.label} as={icon.icon} boxSize="20px" />
      {isOpen && <NavLinkTextWrapper open={isOpen} className={label === 'Saved searches' ? 'ik-saved-search-label' : label === 'Bookmarks' ? 'ik-bookmark-label' : ''}>{label}</NavLinkTextWrapper>}
      <PermissionGuard
        permission={PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE}
      >
        {(hasPermission: boolean) =>
          hasPermission && isOpen && label === 'Bookmarks' ? (
            <IconButton
              ml="auto"
              aria-label={icon.label}
              as={GoPlus}
              boxSize="20px"
              variant="link"
              minWidth="20px"
              height="20px"
              color="appPaleBlue"
              cursor="pointer"
              onClick={e => {
                e.stopPropagation()
                onOpenBookmarks()
              }}
            />
          ) : (
            <></>
          )
        }
      </PermissionGuard>
      {children}
      {showFinishSetupBadge && isOpen && <Badge ml="10px">Finish setup</Badge>}
      {isOpenBookmarks && (
        <AddBookmarkModal
          isOpen={isOpenBookmarks}
          onClose={onCloseBookmarks}
          getAndSetBookmarks={getAndSetBookmarks}
        />
      )}
    </>
  )

  return (
    <NavItemWrapper>
      <Tooltip
        label={label}
        placement="right"
        hasArrow={true}
        isDisabled={Boolean(isOpen)}
      >
        {route === 'drawer' ? (
          <Box
            display="flex"
            align="center"
            className={highlight ? 'navLinkActive' : 'navLink'}
            p="0.5em 0.75em"
            onClick={onClick}
          >
            {content}
          </Box>
        ) : (
          <NavLink
            exact={route === '/'}
            to={route}
            activeClassName="navLinkActive"
            className="navLink"
          >
            {content}
          </NavLink>
        )}
      </Tooltip>
    </NavItemWrapper>
  )
}

const UpgradeBox = ({ isOpen, upgradeText, paidPlanName }: { isOpen: boolean, upgradeText: string, paidPlanName: string }): React.ReactElement => {
  return (
    <Tooltip label={"Upgrade"} placement={"right"} hasArrow={true} isDisabled={Boolean(isOpen)}>
      <NavLink to="/upgrade-plan" className={"upgrade-banner"}>
        <StyledUpgradeBox open={isOpen}>
          <Icon
            aria-label="Upgrade Plan"
            as={Upgrade}
            boxSize="22px"></Icon>

          {isOpen &&
            <Text as="span" fontSize={14}>
              Upgrade to
              <Text as="span" isTruncated fontSize={14} fontWeight="bold">
                &nbsp;{paidPlanName}
              </Text>
              <br></br>
              <Text as="span" fontSize={12} color="#CDD5DF" fontWeight="400">
                {upgradeText}
              </Text>
            </Text>
          }

          {isOpen &&
            <Box as="button" style={{
              backgroundColor: "#0B1B36",
              display: "inline",
              borderRadius: 16,
              padding: "2px 4px",
              width: 100
            }}>
              <NavLink to="/upgrade-plan">
                <Text as="span" fontSize={12} color="white" fontWeight="500">
                  Know more &nbsp;
                  <Icon as={RightArrow} boxSize="8px"></Icon>
                </Text>
              </NavLink>
            </Box>
          }
        </StyledUpgradeBox>
      </NavLink>
    </Tooltip>

  )
}

const NavBar = ({ isOpen }: { isOpen: boolean }): React.ReactElement | null => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)
  const {
    isOpen: isOpenBookmarks,
    onClose: onCloseBookmarks,
    onOpen: onOpenBookmarks,
  } = useDisclosure()
  const {
    isOpen: isOpenSavedSearches,
    onClose: onCloseSavedSearches,
    onOpen: onOpenSavedSearches,
  } = useDisclosure()
  const { productAdoptionMetrics, loading } = useProductOnboardingMetrics();
  const checklistItems = getCheckListItems({ user });
  const allItemsComplete = checklistItems.every(item => {
    return productAdoptionMetrics[item.metricName]
  });
  const DAM_ONLY_PLAN = user?.pricingPlan.planType === "DAM_ONLY"
  const history = useHistory()

  useEffect(() => {
    const unlisten = history.listen(() => {
      onCloseBookmarks()
      onCloseSavedSearches()
    })
    return () => {
      unlisten()
    }
  }, [history])

  if (loading) return null;

  const navBarStaticRoutes = [
    {
      route: '/',
      showFinishSetupBadge: !allItemsComplete,
      icon: { label: 'Home Icon', icon: Home },
      label: allItemsComplete ? 'Home' : 'Get started',
      viewCondition: true,
    },
    {
      route: '/analytics',
      icon: { label: 'Usage Analytics Icon', icon: UsageAnalytics },
      label: t('usage_analytics', 'Usage Analytics'),
      viewCondition: checkPermission({
        user,
        permission: PermissionsEnum.SELF_ANALYTICS_VIEW,
      }),
    },
    {
      route: '/manage-accounts',
      icon: { label: 'Manage Accounts Icon', icon: MdManageAccounts },
      label: t('manage_accounts', 'Manage Accounts'),
      viewCondition: checkPermission({
        user,
        permission: PermissionsEnum.SELF_AGENCY_ANALYTICS_VIEW,
      }),
    },
    {
      route: '/agency-analytics',
      icon: { label: 'Analytics Icon', icon: UsageAnalytics },
      label: t('analytics', 'Analytics'),
      viewCondition: checkPermission({
        user,
        permission: PermissionsEnum.SELF_AGENCY_ANALYTICS_VIEW,
      }),
    },
    {
      route: '/purge-cache',
      icon: { label: 'Purge Cache', icon: PurgeCache },
      label: t('purge_cache', 'Purge Cache'),
      viewCondition: checkPermission({
        user,
        permission: PermissionsEnum.SELF_CLEAR_CACHE_VIEW,
      }),
    },
  ]

  const upgradePlanRoute = {
    route: '/upgrade-plan',
    icon: { label: 'Upgrade Plan', icon: GiUpgrade },
    label: t('upgrade_plan', 'Upgrade Plan'),
    viewCondition: !user?.pricingPlan.isPaidPlan,
  }

  const navBarAccordionRoutes = [
    {
      heading: t('dam', 'Digital Assets Management'),
      viewCondition: checkPermission({
        user,
        permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_VIEW,
      }),
      routes: [
        {
          route: '/media-library',
          icon: { label: 'Media Library Icon', icon: MediaLibrary },
          label: isRestrictedUser(user?.roles)
            ? t('shared_with_me', 'Shared with me')
            : t('media_library', 'Media Library'),
          viewCondition: checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_VIEW,
          }),
        },
        {
          route: '/media-collections',
          icon: { label: 'Media Collections Icon', icon: Collection },
          label: t('media_collections', 'Media collections'),
          viewCondition: checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_VIEW,
          }),
        },
        {
          route: 'drawer',
          icon: { label: 'Saved searches icon', icon: HiMiniBookmark },
          label: t('saved_searches', 'Saved searches'),
          viewCondition: checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_VIEW,
          }),
          highlight: isOpenSavedSearches,
          onClick: () => {
            onCloseBookmarks()

            if (isOpenSavedSearches) {
              onCloseSavedSearches()
            } else {
              onOpenSavedSearches()
            }
          }
        },
        {
          route: 'drawer',
          icon: { label: 'Bookmarks icon', icon: TbLink },
          label: t('bookmarks', 'Bookmarks'),
          viewCondition: checkPermission({
            user,
            permission: PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_VIEW,
          }),
          highlight: isOpenBookmarks,
          onClick: () => {
            onCloseSavedSearches()

            if (isOpenBookmarks) {
              onCloseBookmarks()
            } else {
              onOpenBookmarks()
            }
          }
        },
        {
          route: '/path-policy',
          icon: { label: 'Path policy icon', icon: MdOutlinePolicy },
          label: t('path_policy', 'Path policy'),
          viewCondition: checkPermission({
            user,
            permission: PermissionsEnum.SELF_MEDIA_LIBRARY_VIEW,
          }),
        },
      ],
    },
    {
      heading: t('configuration', 'Configuration'),
      viewCondition:
        checkPermission({
          user,
          permission: PermissionsEnum.SELF_INTEGRATION_VIEW,
        }) ||
        checkPermission({
          user,
          permission: PermissionsEnum.SELF_IMAGE_SETTINGS_VIEW,
        }) ||
        checkPermission({
          user,
          permission: PermissionsEnum.SELF_MEDIA_LIBRARY_MANAGE,
        }),
      routes: [
        {
          route: '/external-storage',
          icon: { label: 'External Storage', icon: ExternalStorage },
          label: t('external_storage', 'External Storage'),
          viewCondition: !DAM_ONLY_PLAN && checkPermission({
            user,
            permission: PermissionsEnum.SELF_INTEGRATION_VIEW,
          }),
        },
        {
          route: '/url-endpoints',
          icon: { label: 'Url endpoint', icon: URLEndpoint },
          label: t('url_endpoints', 'URL Endpoints'),
          viewCondition: !DAM_ONLY_PLAN && checkPermission({
            user,
            permission: PermissionsEnum.SELF_INTEGRATION_VIEW,
          }),
        },
        {
          route: '/settings',
          icon: { label: 'Settings', icon: Setting },
          label: t('settings', 'Settings'),
          viewCondition:
            checkPermission({
              user,
              permission: PermissionsEnum.SELF_IMAGE_SETTINGS_VIEW,
            }) ||
            checkPermission({
              user,
              permission: PermissionsEnum.SELF_MEDIA_LIBRARY_MANAGE,
            }),
        },
      ],
    },
    {
      heading: t('developer', 'Developer'),
      viewCondition:
        !DAM_ONLY_PLAN && (
          checkPermission({
            user,
            permission: PermissionsEnum.SELF_PERFORMANCE_VIEW,
          }) ??
          checkPermission({
            user,
            permission: PermissionsEnum.SELF_DEVELOPERS_VIEW,
          })
        ),
      routes: [
        {
          route: '/performance-alerts',
          icon: { label: 'Performance center', icon: PerformanceCenter },
          label: t('performance_center', 'Performance center'),
          viewCondition: checkPermission({
            user,
            permission: PermissionsEnum.SELF_PERFORMANCE_VIEW,
          }),
        },
        {
          route: '/developer',
          icon: { label: 'Developer Options', icon: DeveloperOptions },
          label: t('developer_options', 'Developer Options'),
          viewCondition: !DAM_ONLY_PLAN && checkPermission({
            user,
            permission: PermissionsEnum.SELF_DEVELOPERS_VIEW,
          }),
        },
      ],
    },
  ]

  const paidPlan = DAM_ONLY_PLAN ? user?.availablePlans.damPremiumPlan : user?.availablePlans.premiumPlan

  const storageInPaidPlan = paidPlan?.inclusionAndOverage?.storage?.inclusion;
  const bandwidthInPaidPlan = paidPlan?.inclusionAndOverage?.bandwidth?.inclusion;
  const paidPlanName = paidPlan?.planName || "Premium";

  const upgradeText = DAM_ONLY_PLAN ? `${storageInPaidPlan}GB storage and all AI features` : `${bandwidthInPaidPlan}GB bandwidth and much more`;

  return (
    <>
      <NavWrapper isOpen={isOpen} role="navigation">
        {upgradePlanRoute.viewCondition ? (
          <UpgradeBox
            isOpen={isOpen}
            upgradeText={upgradeText}
            paidPlanName={paidPlanName}
          ></UpgradeBox>
        ) : null}
        <Box
          key={`nav-group-static`}
          borderTop="1px solid #4B5A74"
          padding={'1em 0'}
        >
          <NavGroupWrapper>
            {navBarStaticRoutes
              .filter(x => x.viewCondition)
              .map(navBarStaticRoute => (
                <NavItem
                  key={`nav-item-${navBarStaticRoute.label}`}
                  {...navBarStaticRoute}
                  isOpen={isOpen}
                />
              ))}
          </NavGroupWrapper>
        </Box>
        {navBarAccordionRoutes
          .filter(x => x.viewCondition)
          .map(navBarAccordionRoute => (
            <Box
              key={`nav-group-${navBarAccordionRoute.heading}`}
              borderTop="1px solid #4B5A74"
              padding={'1em 0'}
            >
              <NavGroupHeading open={isOpen}>
                {navBarAccordionRoute.heading}
              </NavGroupHeading>
              <NavGroupWrapper>
                {navBarAccordionRoute.routes
                  .filter(x => x.viewCondition)
                  .map(route => (
                    <NavItem
                      key={`nav-item-${route.label}`}
                      {...route}
                      isOpen={isOpen}
                    />
                  ))}
              </NavGroupWrapper>
            </Box>
          ))}
      </NavWrapper>
      {isOpenBookmarks && (
        <BookmarksDrawer
          isNavbarOpen={isOpen}
          isOpenBookmarks={isOpenBookmarks}
          onCloseBookmarks={onCloseBookmarks}
        />
      )}
      {isOpenSavedSearches && (
        <SavedSearchesDrawer
          isNavbarOpen={isOpen}
          isOpenSavedSearches={isOpenSavedSearches}
          onCloseSavedSearches={onCloseSavedSearches}
        />
      )}
    </>
  )
}

export default NavBar
